import { interpolate } from '../../../global';
import { ILanguageResources, ITranslationModule } from '../../../interfaces';

class Id {
  private constructor() {}
  public static readonly prefix = 'UI.webmodule-common.other.ui.OfflineOverlay.';
  public static readonly connectingTitle = `${Id.prefix}connectingTitle`;
  public static readonly connectingMessage = `${Id.prefix}connectingMessage`;
  public static readonly connectionLostTitle = `${Id.prefix}connectionLostTitle`;
  public static readonly connectionLostMessage = `${Id.prefix}connectionLostMessage`;
  public static readonly attemptingReconnect = `${Id.prefix}attemptingReconnect`;
  public static readonly extendedOfflineMessage = `${Id.prefix}extendedOfflineMessage`;
  public static readonly retryNow = `${Id.prefix}retryNow`;
  public static readonly retrying = `${Id.prefix}retrying`;
  public static readonly serviceRestartingTitle = `${Id.prefix}serviceRestartingTitle`;
  public static readonly serviceRestartingMessage = `${Id.prefix}serviceRestartingMessage`;
  public static readonly nextRetryIn = `${Id.prefix}nextRetryIn`;
  public static readonly reconnectionAttempts = `${Id.prefix}reconnectionAttempts`;
}

export class LangOfflineOverlay {
  private constructor() {}
  public static connectingTitle(): string {
    return interpolate(Id.connectingTitle);
  }
  public static connectingMessage(): string {
    return interpolate(Id.connectingMessage);
  }
  public static connectionLostTitle(): string {
    return interpolate(Id.connectionLostTitle);
  }
  public static connectionLostMessage(): string {
    return interpolate(Id.connectionLostMessage);
  }
  public static attemptingReconnect(count: number): string {
    return interpolate(Id.attemptingReconnect, { count: count.toString() });
  }
  public static extendedOfflineMessage(): string {
    return interpolate(Id.extendedOfflineMessage);
  }
  public static retryNow(): string {
    return interpolate(Id.retryNow);
  }
  public static retrying(): string {
    return interpolate(Id.retrying);
  }
  public static serviceRestartingTitle(): string {
    return interpolate(Id.serviceRestartingTitle);
  }
  public static serviceRestartingMessage(): string {
    return interpolate(Id.serviceRestartingMessage);
  }
  public static nextRetryIn(seconds: number): string {
    return interpolate(Id.nextRetryIn, { seconds: seconds.toString() });
  }
  public static reconnectionAttempts(count: number): string {
    return interpolate(Id.reconnectionAttempts, { count: count.toString() });
  }
}

export class OfflineOverlayModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    register.add(Id.connectingTitle, 'Connection Interrupted', 'Title when initially trying to connect');
    register.add(
      Id.connectingMessage,
      'Your work is safe. We are automatically reconnecting to the server. Please do not refresh the page as this may cause you to lose unsaved changes.',
      'Message when initially trying to connect'
    );
    register.add(Id.connectionLostTitle, 'Connection Lost', 'Title when connection has been lost for extended time');
    register.add(
      Id.connectionLostMessage,
      'Your work is safe. We are having trouble connecting to the server. Please wait for automatic reconnection - do not refresh the page as this will lose any unsaved changes.',
      'Message when connection has been lost for extended time'
    );
    register.add(
      Id.attemptingReconnect,
      'Attempting to reconnect... (Attempt {{count}})',
      'Status message showing reconnection attempts'
    );
    register.add(
      Id.extendedOfflineMessage,
      'This is taking longer than expected. Your data is still safe - please do not refresh. Check your internet connection or contact support if the issue persists.',
      'Message shown after extended offline period'
    );
    register.add(Id.retryNow, 'Retry Now', 'Button text for manual retry');
    register.add(Id.retrying, 'Retrying...', 'Button text while retrying');
    register.add(Id.serviceRestartingTitle, 'Service Restarting', 'Title when service is restarting');
    register.add(
      Id.serviceRestartingMessage,
      'The service is restarting for maintenance. Your work is safe - please wait for automatic reconnection. Do not refresh the page.',
      'Message when service is restarting'
    );
    register.add(
      Id.nextRetryIn,
      'Next reconnection attempt in {{seconds}}s',
      'Status message showing countdown to next retry'
    );
    register.add(
      Id.reconnectionAttempts,
      'Reconnection attempts: {{count}}',
      'Status message showing total reconnection attempts'
    );
  }
}
